import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import theme from './theme';
import { useLanguage } from './context/LanguageContext';
import Layout from './components/Layout';
import SalesInvoice from './pages/SalesInvoice';
import Customers from './pages/Customers';
import Items from './pages/Items';
import Reports from './pages/Reports';
import ReportForm from './pages/ReportForm';
import SalesSummaryReport from './pages/SalesSummaryReport';
import PaymentCollectionReport from './pages/PaymentCollectionReport';
import VATDetail from "./pages/VAT_Detail";
import CustomerSalesReport from './pages/CustomerSalesReport';
import DSRReport from './pages/DSRReport';
import QuotationReport from './pages/QuotationReport';
import VATPurchaseReport from './pages/VATPurchaseReport';
import VATSalesReport from './pages/VATSalesReport';
import VATReturnReport from './pages/VATReturnReport';
import OtherMasters from './pages/OtherMasters';
import Shortcuts from './pages/Shortcuts';
import Login from './pages/Login';
import Register from './pages/Register';
import NewCustomer from './pages/NewCustomer';
import Suppliers from './pages/Suppliers';
import NewSupplier from './pages/NewSupplier';
import NewItem from './pages/NewItem';
import Sales from './pages/Sales';
import Quotation from './pages/Quotation';
import QuotationForm from './pages/QuotationForm';
import Purchases from './pages/Purchases';
import PurchaseInvoice from './pages/PurchaseInvoice';
import PurchaseReturn from './pages/PurchaseReturn';
import PurchaseReturnForm from './pages/PurchaseReturnForm';
import Documents from './pages/Documents';
import Subscription from './pages/Subscription';
import VATReports from './pages/VATReports';
import Dashboard from './pages/Dashboard';
import CreditNote from './pages/CreditNote';
import DebitNote from './pages/DebitNote';
import NewCreditNote from './pages/NewCreditNote';
import NewDebitNote from './pages/NewDebitNote';
import CompleteRegistration from './pages/CompleteRegistration';
import TeamMembers from './pages/TeamMembers';
import AcceptInvitation from './pages/AcceptInvitation';
import { AuthProvider, useAuth } from './context/AuthContext';
import Agents from './pages/Agents';
import ZatcaOnboarding from './pages/ZatcaOnboarding';
import Settings from './pages/Settings';
import ZatcaDetails from './pages/ZatcaDetails';
import MyCompany from './pages/my-company';

// Protected route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public route component
const PublicRoute = ({ children }) => {
  const { isAuthenticated, currentUser } = useAuth();
  
  if (isAuthenticated) {
    const userRole =
      currentUser?.role ||
      (() => {
        try {
          const stored = localStorage.getItem("user");
          return stored ? JSON.parse(stored)?.role : null;
        } catch {
          return null;
        }
      })();
    const isVatAdmin = typeof userRole === "string" && userRole.trim().toLowerCase() === "vatadmin";
    return <Navigate to={isVatAdmin ? "/my-company" : "/dashboard"} replace />;
  }
  
  return children;
};

// Component to handle index and dashboard route redirection based on role
const DashboardOrRoleRedirect = () => {
  const { currentUser } = useAuth();
  const userRole =
    currentUser?.role ||
    (() => {
      try {
        const stored = localStorage.getItem("user");
        return stored ? JSON.parse(stored)?.role : null;
      } catch {
        return null;
      }
    })();
  const isVatAdmin = typeof userRole === "string" && userRole.trim().toLowerCase() === "vatadmin";
  if (isVatAdmin) {
    return <Navigate to="/my-company" replace />;
  }
  return <Dashboard />;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <Register />
        </PublicRoute>
      } />
      <Route path="/accept-invitation/:token" element={
        <PublicRoute>
          <AcceptInvitation />
        </PublicRoute>
      } />

      {/* Protected routes */}
      <Route path="/" element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<DashboardOrRoleRedirect />} />
        <Route path="dashboard" element={<DashboardOrRoleRedirect />} />
        <Route path="shortcuts" element={<Shortcuts />} />
        <Route path="customers" element={<Customers />} />
        <Route path="customers/new" element={<NewCustomer />} />
        <Route path="customers/edit/:id" element={<NewCustomer />} />
        <Route path="suppliers" element={<Suppliers />} />
        <Route path="suppliers/new" element={<NewSupplier />} />
        <Route path="suppliers/edit/:id" element={<NewSupplier />} />
        <Route path="items" element={<Items />} />
        <Route path="items/new" element={<NewItem />} />
        <Route path="items/edit/:id" element={<NewItem />} />
        <Route path="sales/new-invoice" element={<SalesInvoice />} />
        <Route path="sales/edit-invoice/:id" element={<SalesInvoice />} />
        <Route path="sales" element={<Sales />} />
        <Route path="quotation" element={<Quotation />} />
        <Route path="quotation/new" element={<QuotationForm />} />
        <Route path="quotation/edit/:quotationNo" element={<QuotationForm />} />
        <Route path="purchases" element={<Purchases />} />
        <Route path="purchases/new-invoice" element={<PurchaseInvoice />} />
        <Route path="purchase-invoice" element={<PurchaseInvoice />} />
        <Route path="purchase-invoice/:id" element={<PurchaseInvoice />} />
        <Route path="purchase-return" element={<PurchaseReturn />} />
        <Route path="purchase-returns/view/:invoiceNo" element={<PurchaseReturn />} />
        <Route path="purchase-return/new" element={<PurchaseReturnForm />} />
        <Route path="purchase-return/:id" element={<PurchaseReturnForm />} />
        <Route path="credit-notes" element={<CreditNote />} />
        <Route path="credit-notes/new" element={<NewCreditNote />} />
        <Route path="debit-notes" element={<DebitNote />} />
        <Route path="debit-notes/new" element={<NewDebitNote />} />
        <Route path="reports" element={<Reports />} />
        <Route path="reports/salesSummary" element={<SalesSummaryReport />} />
        <Route path="reports/paymentCollection" element={<PaymentCollectionReport />} />
        <Route path="reports/VAT_Detail" element={<VATDetail />} />
        <Route path="reports/customerSales" element={<CustomerSalesReport />} />
        <Route path="reports/dsr" element={<DSRReport />} />
        <Route path="reports/quotationReport" element={<QuotationReport />} />
        <Route path="reports/vatPurchase" element={<VATPurchaseReport />} />
        <Route path="reports/vatSales" element={<VATSalesReport />} />
        <Route path="reports/vatReturn" element={<VATReturnReport />} />
        <Route path="reports/:reportType" element={<ReportForm />} />
        <Route path="documents" element={<Documents />} />
        <Route path="other-masters" element={<OtherMasters />} />
        <Route path="subscription" element={<Subscription />} />
        <Route path="vat-reports" element={<VATReports />} />
        <Route path="complete-registration" element={<CompleteRegistration />} />
        <Route path="team-members" element={<TeamMembers />} />
        <Route path="my-company" element={<MyCompany />} />
        <Route path="agents" element={<Agents />} />
        <Route path="zatca-onboarding" element={<ZatcaOnboarding />} />
        <Route path="zatca-details" element={<ZatcaDetails />} />
        <Route path="settings" element={<Settings />} />
        <Route path="*" element={<DashboardOrRoleRedirect />} />
      </Route>
    </Routes>
  );
}

const cacheRtl = createCache({
  key: 'muirtl',
  stylisPlugins: [prefixer, rtlPlugin],
});

const cacheLtr = createCache({
  key: 'muiltr',
});

function App() {
  const { language } = useLanguage();
  const isRtl = language === 'ar';

  const appTheme = React.useMemo(() => 
    createTheme(theme, {
      direction: isRtl ? 'rtl' : 'ltr',
    }), [isRtl, theme]);

  return (
    <CacheProvider value={isRtl ? cacheRtl : cacheLtr}>
      <ThemeProvider theme={appTheme}>
        <CssBaseline />
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </ThemeProvider>
    </CacheProvider>
  );
}

export { AppRoutes };
export default App;